import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'
import axios from 'axios'
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from '../utils/localStorage'
import { MAINTENANCE_MODE } from '../config/maintenanceAccess'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

const AuthContext = createContext(null)

export const useAuthContext = () => useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStorageItem('authToken'))
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [maintenanceRestricted, setMaintenanceRestricted] = useState(false)

  // --- NEW: Privacy Status State ---
  const [privacyStatus, setPrivacyStatus] = useState({ agreed: false, version: null })
  const [checkingPrivacy, setCheckingPrivacy] = useState(true)

  // --- NEW: Function to check privacy status ---
  const checkPrivacyStatus = useCallback(async currentToken => {
    if (!currentToken) {
      setCheckingPrivacy(false)
      return
    }
    setCheckingPrivacy(true)
    try {
      const response = await axios.get(`${API_BASE_URL}/users/me/privacy_status`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      setPrivacyStatus({
        agreed: response.data.agreed,
        version: response.data.policy_version,
      })
    } catch (error) {
      console.error('Failed to check privacy status:', error)
      // Assume not agreed if check fails
      setPrivacyStatus({ agreed: false, version: null })
    } finally {
      setCheckingPrivacy(false)
    }
  }, [])

  const fetchUser = useCallback(
    async currentToken => {
      if (!currentToken) {
        setToken(null)
        setUser(null)
        setIsAuthenticated(false)
        setLoading(false)
        setCheckingPrivacy(false)
        return
      }
      try {
        setLoading(true)
        setMaintenanceRestricted(false)
        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        setUser(response.data)
        setIsAuthenticated(true)
        // After fetching user, check their privacy status
        await checkPrivacyStatus(currentToken)
      } catch (error) {
        const isMaintenanceRestriction =
          MAINTENANCE_MODE &&
          error?.response?.status === 403 &&
          error?.response?.data?.detail === 'maintenance_restricted'

        if (isMaintenanceRestriction) {
          setMaintenanceRestricted(true)
          setIsAuthenticated(true)
          setUser(null)
          setCheckingPrivacy(false)
          return
        }

        console.error('Failed to fetch user:', error)
        removeStorageItem('authToken')
        setToken(null)
        setUser(null)
        setIsAuthenticated(false)
        setMaintenanceRestricted(false)
        setPrivacyStatus({ agreed: false, version: null })
        setCheckingPrivacy(false)
      } finally {
        setLoading(false)
      }
    },
    [checkPrivacyStatus]
  )

  useEffect(() => {
    const storedToken = getStorageItem('authToken')
    if (storedToken) {
      setToken(storedToken)
      fetchUser(storedToken)
    } else {
      setLoading(false)
      setCheckingPrivacy(false)
    }
  }, [fetchUser])

  const login = useCallback(
    newToken => {
      setStorageItem('authToken', newToken)
      setToken(newToken)
      fetchUser(newToken)
      // No need to set isAuthenticated here, fetchUser will do it
    },
    [fetchUser]
  )

  const logout = useCallback(() => {
    removeStorageItem('authToken')
    setToken(null)
    setUser(null)
    setIsAuthenticated(false)
    setMaintenanceRestricted(false)
    setPrivacyStatus({ agreed: false, version: null })
    setLoading(false)
    setCheckingPrivacy(false)
  }, [])

  const value = {
    user,
    token,
    isAuthenticated,
    maintenanceRestricted,
    loading,
    checkingPrivacy,
    privacyStatus,
    login,
    logout,
    fetchUser, // <-- EXPOSED fetchUser
    checkPrivacyStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
